import React from 'react';
import { Typography, Tag } from 'antd';

const { Text } = Typography;

export const InlineMarkdown: React.FC<{ text: string }> = ({ text }) => {
    const parts: React.ReactNode[] = [];
    const re = /(\*\*(.+?)\*\*|`([^`]+)`)/g;
    let last = 0;
    let match: RegExpExecArray | null;
    let key = 0;
    while ((match = re.exec(text)) !== null) {
        if (match.index > last) parts.push(text.slice(last, match.index));
        if (match[2]) parts.push(<strong key={key++}>{match[2]}</strong>);
        else if (match[3]) parts.push(<code key={key++} style={{ padding: '1px 5px', background: '#f0f0f0', borderRadius: 3, fontSize: '0.9em', fontFamily: "'SF Mono', monospace" }}>{match[3]}</code>);
        last = match.index + match[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return <>{parts}</>;
};

export const SimpleMarkdown: React.FC<{ text: string }> = ({ text }) => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];

        if (line.trimStart().startsWith('```')) {
            const codeLines: string[] = [];
            const lang = line.trim().slice(3);
            i++;
            while (i < lines.length && !lines[i].trimStart().startsWith('```')) {
                codeLines.push(lines[i]);
                i++;
            }
            i++;
            elements.push(
                <pre key={elements.length} style={{
                    margin: '8px 0',
                    padding: 12,
                    background: '#1e1e1e',
                    color: '#d4d4d4',
                    borderRadius: 6,
                    fontSize: 12,
                    lineHeight: 1.5,
                    overflow: 'auto',
                    fontFamily: "'SF Mono', 'Fira Code', monospace",
                }}>
                    {lang && <div style={{ color: '#888', fontSize: 11, marginBottom: 4 }}>{lang}</div>}
                    {codeLines.join('\n')}
                </pre>,
            );
            continue;
        }

        if (line.includes('|') && i + 1 < lines.length && /^\s*\|[-:|  ]+\|\s*$/.test(lines[i + 1])) {
            const tableLines: string[] = [];
            while (i < lines.length && lines[i].includes('|')) {
                tableLines.push(lines[i]);
                i++;
            }
            const parseRow = (r: string) =>
                r.split('|').map((c) => c.trim()).filter(Boolean);
            const headerCols = parseRow(tableLines[0]);
            const bodyRows = tableLines.slice(2).map(parseRow);
            elements.push(
                <div key={elements.length} style={{ overflowX: 'auto', margin: '8px 0' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                        <thead>
                            <tr>
                                {headerCols.map((h, hi) => (
                                    <th key={hi} style={{
                                        padding: '6px 10px',
                                        borderBottom: '2px solid #e8e8e8',
                                        textAlign: 'left',
                                        fontWeight: 600,
                                        background: '#fafafa',
                                        whiteSpace: 'nowrap',
                                    }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {bodyRows.map((row, ri) => (
                                <tr key={ri}>
                                    {row.map((cell, ci) => (
                                        <td key={ci} style={{
                                            padding: '5px 10px',
                                            borderBottom: '1px solid #f0f0f0',
                                            fontSize: 12,
                                        }}>
                                            <InlineMarkdown text={cell} />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>,
            );
            continue;
        }

        if (line.startsWith('### ')) {
            elements.push(<h4 key={elements.length} style={{ margin: '16px 0 6px', fontSize: 14, fontWeight: 600 }}>{line.slice(4)}</h4>);
            i++; continue;
        }
        if (line.startsWith('## ')) {
            elements.push(<h3 key={elements.length} style={{ margin: '20px 0 8px', fontSize: 16, fontWeight: 600, borderBottom: '1px solid #e8e8e8', paddingBottom: 4 }}>{line.slice(3)}</h3>);
            i++; continue;
        }
        if (line.startsWith('# ')) {
            elements.push(<h2 key={elements.length} style={{ margin: '20px 0 8px', fontSize: 18, fontWeight: 700 }}>{line.slice(2)}</h2>);
            i++; continue;
        }

        if (/^---+$/.test(line.trim())) {
            elements.push(<hr key={elements.length} style={{ border: 'none', borderTop: '1px solid #e8e8e8', margin: '12px 0' }} />);
            i++; continue;
        }

        if (/^[-*] /.test(line.trimStart())) {
            const indent = line.length - line.trimStart().length;
            elements.push(
                <div key={elements.length} style={{ paddingLeft: indent + 12, marginBottom: 2, fontSize: 13, display: 'flex', gap: 6 }}>
                    <span style={{ color: '#999' }}>&bull;</span>
                    <span><InlineMarkdown text={line.trimStart().slice(2)} /></span>
                </div>,
            );
            i++; continue;
        }

        if (line.trim() === '') {
            elements.push(<div key={elements.length} style={{ height: 6 }} />);
            i++; continue;
        }

        elements.push(<p key={elements.length} style={{ margin: '4px 0', fontSize: 13, lineHeight: 1.6 }}><InlineMarkdown text={line} /></p>);
        i++;
    }

    return <>{elements}</>;
};

export const renderValue = (value: unknown, depth = 0): React.ReactNode => {
    if (value == null) return <Text type="secondary">null</Text>;

    if (typeof value === 'string') {
        if (value.includes('\n') || value.length > 120) {
            return <SimpleMarkdown text={value} />;
        }
        return <Text style={{ fontSize: 13 }}>{value}</Text>;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return <Tag color="blue">{String(value)}</Tag>;
    }

    if (Array.isArray(value)) {
        if (value.length === 0) return <Text type="secondary">[]</Text>;
        return (
            <div style={{ marginLeft: depth > 0 ? 16 : 0, marginTop: 4 }}>
                {value.map((item, i) => (
                    <div key={i} style={{ marginBottom: 4, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                        <Text type="secondary" style={{ fontSize: 11, minWidth: 18 }}>{i + 1}.</Text>
                        <div style={{ flex: 1 }}>{renderValue(item, depth + 1)}</div>
                    </div>
                ))}
            </div>
        );
    }

    if (typeof value === 'object') {
        const entries = Object.entries(value as Record<string, unknown>);
        return (
            <div style={{ marginLeft: depth > 0 ? 16 : 0, marginTop: depth > 0 ? 4 : 0 }}>
                {entries.map(([k, v]) => (
                    <div key={k} style={{ marginBottom: 8 }}>
                        <Text strong style={{ fontSize: 13, color: '#1d1d1f' }}>{k}</Text>
                        <div style={{ marginTop: 2 }}>{renderValue(v, depth + 1)}</div>
                    </div>
                ))}
            </div>
        );
    }

    return <Text>{String(value)}</Text>;
};
